/**
 * LDSU to Auxiliary Hub Transformers
 * 
 * This file contains functions to transform LDSU (Local Data Service Unit) data
 * into Auxiliary Hub format for display in the network details pages.
 * 
 * Functions:
 * - transformLdsuToAuxiliaryHub: Transforms raw LDSU data to Auxiliary Hub format
 * - getAuxiliaryHubsFromLdsus: Gets Auxiliary Hubs from LDSUs for a specific bus
 */

import { LdsuDto } from "types/Ldsu";
import { DeviceConfig as AppDeviceConfig, GatewayReadingDevice } from "types/Gateways";
import { DeviceConfig as GeneratedDeviceConfig } from "generated/models";
import { uniqBy } from "lodash";
import { checkIfReachable } from "utils/functions";
import {
    isAuxiliaryHub,
    isRoomOrZoneByName,
    isGroupOrHubByName,
} from "./common/deviceUtils";

/**
 * Transform LDSU data to Auxiliary Hub format
 * @param ldsuData - Raw LDSU data from API (combined from registry, config, and status)
 * @param configMap - Optional config map to get reporting interval (INTVL) for reachability check
 * @returns Transformed data in Auxiliary Hub format
 */
export const transformLdsuToAuxiliaryHub = (ldsuData: any, configMap?: any) => {

    // Calculate status based on timestamp (ts) if available
    // Similar to how GatewayDetails.tsx determines device status
    let status = "Reachable"; // Default
    if (ldsuData.ts !== undefined && ldsuData.ts !== null) {
        // Use a fixed 45s reachability window via checkIfReachable.
        // With n = 1.5 and offset = 30 in checkIfReachable,
        // reportingIntvl = 20 => 20 * 1.5 + 30 = 60 seconds.
        const reportingIntvl = 20;
        const isReachable = checkIfReachable(ldsuData.ts, reportingIntvl);
        status = isReachable ? "Reachable" : "Not Reachable";
    } else if (ldsuData.status) {
        // Fallback to existing status if timestamp is not available
        if (ldsuData.status === "Offline") {
            status = "Not Reachable";
        } else if (ldsuData.status !== "Online") {
            status = ldsuData.status;
        }
        // "Online" maps to "Reachable" which is already the default
    }

    return {
        name: ldsuData.name ?? ldsuData.NAME ?? ldsuData.NICK ?? "-",
        uuid: ldsuData.UID ?? "",
        serialNumber: ldsuData.SNO ?? ldsuData.serialNumber ?? "",
        productName: ldsuData.NAME ?? ldsuData.productName ?? "-",
        manufacturingDate: ldsuData.MFG ?? ldsuData.manufacturingDate ?? "",
        productVersion: ldsuData.PRV ?? ldsuData.productVersion ?? "",
        firmwareVersion: ldsuData.FWV ?? ldsuData.firmwareVersion ?? "",
        ldsuDid: ldsuData.DID ?? ldsuData.ldsuDid ?? "<DID>",
        terminationStatus: ldsuData.TERM ?? ldsuData.terminationStatus ?? "",
        status: status,
        type: "Auxiliary Hub",
        hub_type: "Auxiliary Hub",
        lastActive: ldsuData.lastActive ?? "-",
        networkName: ldsuData.networkName ?? "-",
        // Additional LDSU-specific fields as strings
        port: String(ldsuData.PORT ?? ldsuData.port ?? ""),
        iid: String(ldsuData.IID ?? ldsuData.iid ?? ""),
        obj: String(ldsuData.OBJ ?? ldsuData.obj ?? ""),
        app: ldsuData.APP ?? ldsuData.app ? String(ldsuData.APP ?? ldsuData.app) : "",
        association: ldsuData.association ? String(ldsuData.association) : "",
        // Store original data as a flag instead of the full object
        hasOriginalLdsuData: true
    };
};

/**
 * Get LDSU list (reusing logic from gatewayFunctions.tsx)
 * @param registryInfoList - Registry info list
 * @param configList - Config list
 * @param gwLDSUStatusList - Gateway LDSU status list
 * @returns Combined LDSU list
 */
const getLdsuList = (
    registryInfoList: LdsuDto[],
    configList: (AppDeviceConfig | GeneratedDeviceConfig)[],
    gwLDSUStatusList: GatewayReadingDevice[]
) => {
    const combined: Record<string, any> = {};
    for (const arr of [registryInfoList, configList, gwLDSUStatusList]) {
        for (const item of arr) {
            if (combined[item.UID]) {
                Object.assign(combined[item.UID], item);
            } else {
                combined[item.UID] = item;
            }
        }
    }

    return uniqBy(Object.values(combined), "uuid");
};

/**
 * Get Auxiliary Hubs from LDSUs for a specific bus
 * Filters LDSUs by OBJ value to only include those matching auxiliary hub hex values
 * @param busNumber - Bus number to filter by
 * @param registryInfoList - Registry info list
 * @param configList - Config list
 * @param gwLDSUStatusList - Gateway LDSU status list (contains timestamp 'ts' for reachability check)
 * @returns Array of transformed Auxiliary Hub data
 */
export const getAuxiliaryHubsFromLdsus = (
    busNumber: string,
    registryInfoList: LdsuDto[],
    configList: (AppDeviceConfig | GeneratedDeviceConfig)[],
    gwLDSUStatusList: GatewayReadingDevice[]
) => {
    const ldsuList = getLdsuList(
        registryInfoList,
        configList,
        gwLDSUStatusList
    ).filter((l: any) => {
        // Filter by bus number
        if (l.PORT !== busNumber) {
            return false;
        }

        // Filter by OBJ value - only include devices that qualify as auxiliary hubs
        const obj = l.OBJ ?? l.obj;
        if (!isAuxiliaryHub(obj)) {
            return false;
        }

        // Exclude room/group/zone/hub entity devices based on NAME/NICK
        const name = l.NAME ?? l.NICK ?? "";
        if (isRoomOrZoneByName(name) || isGroupOrHubByName(name)) {
            return false;
        }

        return true;
    });

    // Create a config map for quick lookup of reporting intervals
    const configMap: Record<string, any> = {};
    for (const config of configList) {
        if (config.UID) {
            configMap[config.UID] = config;
        }
    }

    // Transform each LDSU to Auxiliary Hub format
    // Pass configMap to calculate reachability status from timestamp
    return ldsuList.map(ldsu => transformLdsuToAuxiliaryHub(ldsu, configMap));
}; 